import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users, 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  Loader2,
  Shield,
  ShieldCheck,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';

const AdminUsersPage = () => {
  const { api } = useAuth();
  
  // Mark this page as visited
  useMarkPageVisited('/admin/users');
  
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    team_id: '',
    is_responsable: false,
    is_admin: false,
    cc_email: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        api.get('/users'),
        api.get('/teams')
      ]);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(search.toLowerCase()) ||
    user.first_name.toLowerCase().includes(search.toLowerCase()) ||
    user.last_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone || '',
        team_id: user.team_id || '',
        is_responsable: user.is_responsable,
        is_admin: user.is_admin,
        cc_email: user.cc_email || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        team_id: '',
        is_responsable: false,
        is_admin: false,
        cc_email: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Le mot de passe est obligatoire pour un nouvel utilisateur');
      return;
    }

    if (formData.password && formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Adresse email invalide');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password || "",
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || "",
        team_id: formData.team_id || null,
        is_responsable: formData.is_responsable,
        is_admin: formData.is_admin,
        cc_email: formData.is_admin ? (formData.cc_email || null) : null
      };

      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, payload);
        toast.success('Utilisateur modifié');
      } else {
        await api.post('/auth/register', payload);
        toast.success('Utilisateur créé');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('User operation error:', error);
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'opération'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Utilisateurs</h1>
          <p className="text-gray-500 mt-1">Gestion des comptes utilisateurs</p>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Créez et gérez les comptes de votre équipe. Définissez les rôles (Admin, Responsable, Utilisateur) et leurs accès.
          </p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un utilisateur
        </Button>
      </div>

      {/* Explication des rôles */}
      <Card className="bg-gradient-to-br from-[#D8F3DC] to-white border-[#1B4332]/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#1B4332] flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Rôles et permissions</h3>
              <p className="text-sm text-gray-600 mt-1">Chaque rôle dispose de permissions spécifiques</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {/* Admin */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span className="font-semibold text-gray-900">Admin</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">Accès complet et configuration</p>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Gestion des utilisateurs et équipes</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Configuration fournisseurs et produits</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Accès aux paramètres et statistiques</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-blue-600 mt-0.5">⚙</span>
                  <span className="font-medium">Choix de recevoir les validations & CC mails</span>
                </li>
              </ul>
            </div>

            {/* Responsable */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                <span className="font-semibold text-gray-900">Responsable</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">Validation et opérationnel</p>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Passer des commandes</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Réceptionner les livraisons</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Consulter les statistiques</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-orange-600 mt-0.5 font-bold">!</span>
                  <span className="font-medium">Reçoit automatiquement les validations & CC mails</span>
                </li>
              </ul>
            </div>

            {/* Utilisateur */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="font-semibold text-gray-900">Utilisateur</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">Opérations de base uniquement</p>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Passer des commandes</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Réceptionner les livraisons</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span className="opacity-60">Pas de statistiques</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span className="opacity-60">Pas de validation ni CC mails</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Note explicative sur la validation */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-900">
              <span className="font-semibold">📧 Validation des commandes :</span> Les <span className="font-medium">Responsables</span> reçoivent automatiquement les demandes de validation et le CC des mails fournisseurs. Les <span className="font-medium">Admins</span> peuvent activer/désactiver ces notifications dans les Paramètres selon leur niveau d'implication opérationnelle.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder="Rechercher un utilisateur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="users-search-input"
        />
      </div>

      <div className="grid gap-4">
        {filteredUsers.map((user) => (
          <Card key={user.id} className="border border-gray-200" data-testid={`user-card-${user.email}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D8F3DC] flex items-center justify-center">
                    {user.is_admin ? (
                      <ShieldCheck className="w-6 h-6 text-[#1B4332]" />
                    ) : user.is_responsable ? (
                      <Shield className="w-6 h-6 text-[#1B4332]" />
                    ) : (
                      <Users className="w-6 h-6 text-[#1B4332]" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {user.first_name} {user.last_name}
                    </h3>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {user.is_admin && (
                        <Badge className="bg-[#1B4332] text-white">Admin</Badge>
                      )}
                      {user.is_responsable && !user.is_admin && (
                        <Badge variant="outline" className="border-[#1B4332] text-[#1B4332]">Responsable</Badge>
                      )}
                      {user.team_name && (
                        <Badge variant="secondary">{user.team_name}</Badge>
                      )}
                      {user.cc_email && (
                        <Badge variant="outline" className="border-blue-400 text-blue-600 bg-blue-50">
                          <Mail className="w-3 h-3 mr-1" />
                          CC
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={() => handleOpenDialog(user)}
                    data-testid={`edit-user-${user.email}-btn`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="outline"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(user.id)}
                    data-testid={`delete-user-${user.email}-btn`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? 'Modifiez les informations de l\'utilisateur' : 'Créez un nouveau compte utilisateur'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Prénom *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="user-first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  data-testid="user-last-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                Mot de passe {editingUser ? '(laisser vide pour ne pas changer)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                data-testid="user-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="user-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="team">Équipe</Label>
              <Select
                value={formData.team_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, team_id: value === "none" ? null : value })}
              >
                <SelectTrigger data-testid="user-team-select">
                  <SelectValue placeholder="Sélectionner une équipe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune équipe</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_responsable"
                  checked={formData.is_responsable}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_responsable: checked })}
                  data-testid="user-responsable-checkbox"
                />
                <Label htmlFor="is_responsable">Responsable</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_admin"
                  checked={formData.is_admin}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked, cc_email: checked ? formData.cc_email : '' })}
                  data-testid="user-admin-checkbox"
                />
                <Label htmlFor="is_admin">Administrateur</Label>
              </div>
            </div>

            {formData.is_admin && (
              <div className="space-y-2 p-3 bg-[#D8F3DC]/30 rounded-lg border border-[#1B4332]/20">
                <Label htmlFor="cc_email" className="flex items-center gap-2 text-[#1B4332]">
                  <Mail className="w-4 h-4" />
                  Email CC commandes
                </Label>
                <Input
                  id="cc_email"
                  type="email"
                  placeholder="Recevoir une copie des commandes envoyées"
                  value={formData.cc_email}
                  onChange={(e) => setFormData({ ...formData, cc_email: e.target.value })}
                  data-testid="user-cc-email-input"
                />
                <p className="text-xs text-gray-500">Cet email recevra une copie de chaque commande envoyée aux fournisseurs</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={formLoading}
              className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
              data-testid="save-user-btn"
            >
              {formLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
